/**
 * ContactForm Module
 *
 * Client-side form validation with accessible error messages and
 * submit-state management.  Works with any generic contact form and
 * is compatible with server-side handlers (REST, admin-ajax, CF7,
 * WPForms, etc.).
 *
 * @module ContactForm
 */
export default class ContactForm {

  static DEFAULTS = {
    selectors: {
      form: '[data-contact-form]',
      fields: '[data-field]',
      submit: '[data-contact-form] [type="submit"]',
      messages: '[data-form-messages]',
    },
    patterns: {
      email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
      phone: /^[+]?[\d\s()./-]{7,20}$/,
    },
    messages: {
      required: 'Ce champ est requis.',
      email: 'Veuillez saisir une adresse e-mail valide.',
      phone: 'Veuillez saisir un numero de telephone valide.',
      submitSuccess: 'Votre message a bien ete envoye.',
      submitError: 'Une erreur est survenue. Veuillez reessayer.',
    },
    submitEndpoint: null, // set to a URL to handle submission via fetch
  };

  /**
   * @param {Object} options - Merged with DEFAULTS.
   */
  constructor(options = {}) {
    this.config = { ...ContactForm.DEFAULTS, ...options };

    this.form = document.querySelector(this.config.selectors.form);
    if (!this.form) {
      return;
    }

    this.fields = this.form.querySelectorAll(this.config.selectors.fields);
    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.messagesContainer = this.form.querySelector(this.config.selectors.messages)
      || this._createMessagesContainer();

    this._init();
  }

  /* ------------------------------------------------------------------
   * Bootstrap
   * ----------------------------------------------------------------*/

  _init() {
    this._initState();
    this._bindEvents();
  }

  _initState() {
    this.form.setAttribute('data-state', 'idle');
    this.form.setAttribute('novalidate', '');

    this.fields.forEach((field) => {
      field.setAttribute('data-state', 'idle');
      this._ensureErrorContainer(field);
    });
  }

  /* ------------------------------------------------------------------
   * Event binding
   * ----------------------------------------------------------------*/

  _bindEvents() {
    this.fields.forEach((field) => {
      field.addEventListener('focus', this._handleFocus.bind(this));
      field.addEventListener('blur', this._handleBlur.bind(this));
      field.addEventListener('input', this._handleInput.bind(this));
    });

    this.form.addEventListener('submit', this._handleSubmit.bind(this));
  }

  /* ------------------------------------------------------------------
   * Field state transitions
   * ----------------------------------------------------------------*/

  _handleFocus(event) {
    const field = event.target;
    field.setAttribute('data-state', 'focus');
  }

  _handleBlur(event) {
    const field = event.target;
    this._validateField(field);
  }

  _handleInput(event) {
    const field = event.target;

    // Clear error state as soon as the user starts correcting
    if (field.getAttribute('data-state') === 'invalid') {
      this._validateField(field);
    }
  }

  /* ------------------------------------------------------------------
   * Validation
   * ----------------------------------------------------------------*/

  /**
   * Validate a single field and update its data-state and ARIA attrs.
   *
   * @param  {HTMLElement} field
   * @return {boolean}     true when the field is valid.
   */
  _validateField(field) {
    const value = field.value.trim();
    const type = field.getAttribute('data-field');
    const isRequired = field.hasAttribute('required') || field.hasAttribute('data-required');
    let errorMessage = '';

    // Required check
    if (isRequired && value === '') {
      errorMessage = this.config.messages.required;
    }

    // Type-specific pattern checks
    if (!errorMessage && value !== '') {
      if (type === 'email' && !this.config.patterns.email.test(value)) {
        errorMessage = this.config.messages.email;
      }
      if (type === 'phone' && !this.config.patterns.phone.test(value)) {
        errorMessage = this.config.messages.phone;
      }
    }

    if (errorMessage) {
      this._setFieldInvalid(field, errorMessage);
      return false;
    }

    this._setFieldValid(field);
    return true;
  }

  _setFieldInvalid(field, message) {
    field.setAttribute('data-state', 'invalid');
    field.setAttribute('aria-invalid', 'true');

    const errorEl = this._getErrorContainer(field);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      field.setAttribute('aria-describedby', errorEl.id);
    }
  }

  _setFieldValid(field) {
    field.setAttribute('data-state', 'valid');
    field.removeAttribute('aria-invalid');

    const errorEl = this._getErrorContainer(field);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
      field.removeAttribute('aria-describedby');
    }
  }

  /**
   * Validate every field in the form.
   *
   * @return {boolean} true when the entire form is valid.
   */
  _validateAll() {
    let isFormValid = true;
    let firstInvalid = null;

    this.fields.forEach((field) => {
      const valid = this._validateField(field);
      if (!valid && !firstInvalid) {
        firstInvalid = field;
      }
      if (!valid) {
        isFormValid = false;
      }
    });

    // Focus the first invalid field for accessibility
    if (firstInvalid) {
      firstInvalid.focus();
    }

    return isFormValid;
  }

  /* ------------------------------------------------------------------
   * Form submission
   * ----------------------------------------------------------------*/

  async _handleSubmit(event) {
    event.preventDefault();

    // Prevent double-submission
    if (this.form.getAttribute('data-state') === 'submitting') {
      return;
    }

    if (!this._validateAll()) {
      return;
    }

    this.form.setAttribute('data-state', 'submitting');
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
    }

    try {
      if (this.config.submitEndpoint) {
        await this._submitViaFetch();
      } else {
        // Without a configured endpoint fall back to native form
        // submission so server-side plugins (CF7, etc.) can handle it.
        this.form.setAttribute('data-state', 'idle');
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
        }
        this.form.submit();
        return;
      }

      this._showSuccess();
    } catch (error) {
      this._showError(error);
    }
  }

  async _submitViaFetch() {
    const formData = new FormData(this.form);

    const response = await fetch(this.config.submitEndpoint, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /* ------------------------------------------------------------------
   * Success / Error states
   * ----------------------------------------------------------------*/

  _showSuccess() {
    this.form.setAttribute('data-state', 'success');
    this.form.reset();

    // Reset individual field states
    this.fields.forEach((field) => {
      field.setAttribute('data-state', 'idle');
      field.removeAttribute('aria-invalid');
    });

    this._setMessage(this.config.messages.submitSuccess, 'success');

    if (this.submitBtn) {
      this.submitBtn.disabled = false;
    }
  }

  _showError(error) {
    this.form.setAttribute('data-state', 'error');

    const message = error && error.message
      ? `${this.config.messages.submitError} (${error.message})`
      : this.config.messages.submitError;

    this._setMessage(message, 'error');

    if (this.submitBtn) {
      this.submitBtn.disabled = false;
    }
  }

  _setMessage(text, type) {
    this.messagesContainer.textContent = text;
    this.messagesContainer.setAttribute('data-message-type', type);
    this.messagesContainer.setAttribute('role', 'alert');
    this.messagesContainer.hidden = false;
  }

  /* ------------------------------------------------------------------
   * DOM helpers
   * ----------------------------------------------------------------*/

  /**
   * Ensure each field has an associated error container for ARIA
   * live messaging.
   */
  _ensureErrorContainer(field) {
    const id = field.id || `field-${Math.random().toString(36).slice(2, 9)}`;
    field.id = id;

    const errorId = `${id}-error`;

    if (document.getElementById(errorId)) {
      return;
    }

    const span = document.createElement('span');
    span.id = errorId;
    span.className = 'field-error';
    span.setAttribute('role', 'alert');
    span.setAttribute('aria-live', 'polite');
    span.hidden = true;

    // Insert after the field (or its wrapper if inside a label)
    const parent = field.closest('label') || field.parentNode;
    parent.appendChild(span);
  }

  _getErrorContainer(field) {
    const errorId = `${field.id}-error`;
    return document.getElementById(errorId);
  }

  _createMessagesContainer() {
    const div = document.createElement('div');
    div.setAttribute('data-form-messages', '');
    div.setAttribute('role', 'status');
    div.setAttribute('aria-live', 'polite');
    div.hidden = true;
    this.form.appendChild(div);
    return div;
  }

  /* ------------------------------------------------------------------
   * Public API
   * ----------------------------------------------------------------*/

  /** Tear down listeners. */
  destroy() {
    this.fields.forEach((field) => {
      field.removeEventListener('focus', this._handleFocus);
      field.removeEventListener('blur', this._handleBlur);
      field.removeEventListener('input', this._handleInput);
    });

    this.form.removeEventListener('submit', this._handleSubmit);
  }
}
